import moment from "moment";
import { MetricKey, ReportResponse, ReportRow } from "../api/types";
import { formatDay } from "./format";

// ============================================================================
// Hourly chart/grid helpers for Net Hourly & Cumulative (RP-2162 / RP-2163).
//
// Engine contract (landed): docs/Reports-API-Contract-Sprint2-QA.md §2–3 in
// Distributed-Systems.
//   • Aggregate shape (no dimension): always exactly 24 zero-filled rows,
//     key = "14", label = "14:00", group = null.
//   • dimension=Day: one zero-filled 24-row block per day, key =
//     "yyyy-MM-dd:HH", label = "HH:00", group = the day key; hours within a
//     block arrive in 5 AM operating-day order (05…23, 00…04). Capped at
//     100 days (HTTP 400 beyond — the page pre-guards).
//   • CumulativeHourly: same shapes; the running total RESETS per day block.
//
// The OLD reports draw ONE LINE PER DAY (fixed palette) on that same
// boundary→boundary axis — buildHourlySeries reproduces that from the by-day
// shape, and still handles the aggregate shape (single series, plus the
// compare overlay the page adds).
// ============================================================================

// Old reports' per-day line palette, in day order.
export const DAY_SERIES_COLORS = [
  "#B71C1C",
  "#000000",
  "#1C1CB7",
  "#7C1CB7",
  "#B76A1C",
  "#1C96B7",
  "#0B5E0B",
];

export const AGGREGATE_SERIES_KEY = "current";

// The by-day shape zero-fills 24 rows per day server-side and is capped there.
export const MAX_BY_DAY_HOURLY_DAYS = 100;

// Parse the leading hour out of the engine's echoed dayBoundary
// (e.g. "5 AM local business day (America/Chicago)") — falls back to 5,
// the boundary the old reports hard-code.
export function boundaryHourFrom(dayBoundary: string | undefined): number {
  const m = /^\s*(\d{1,2})\s*(AM|PM)/i.exec(dayBoundary ?? "");
  if (!m) return 5;
  const h = parseInt(m[1], 10) % 12;
  return /pm/i.test(m[2]) ? h + 12 : h;
}

// Clock hour of a row. Aggregate keys are "14"; by-day keys are "yyyy-MM-dd:14"
// (hour after the last colon). Label ("14:00") is the fallback.
export function hourFromRow(r: ReportRow): number {
  const key = r.key ?? "";
  const tail = key.includes(":") ? key.slice(key.lastIndexOf(":") + 1) : key;
  const h = parseInt(tail, 10);
  return Number.isFinite(h) ? h : parseInt(r.label, 10) || 0;
}

// Uniform hour label ("5 AM", "12 PM") — used for every slot, so engine rows
// and anything client-filled read identically.
export function hourLabel(hour: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12} ${hour < 12 ? "AM" : "PM"}`;
}

// Short day label for the chart legend (group is a business day, yyyy-MM-dd).
function dayLabel(group: string): string {
  const d = moment.utc(group, "YYYY-MM-DD", true);
  return d.isValid() ? d.format("ddd, MMM D") : group;
}

export interface HourlyPoint {
  slot: number; // 0..23, business-day order (slot 0 = boundary hour)
  hour: number; // clock hour 0..23
  label: string;
  // one numeric entry per series key (day label, or AGGREGATE_SERIES_KEY)
  [seriesKey: string]: number | string;
}

export interface HourlySeries {
  points: HourlyPoint[]; // always 24, boundary→boundary order
  seriesKeys: string[]; // day labels in date order, or [AGGREGATE_SERIES_KEY]
  perDay: boolean; // rows carried a business-day group
  boundaryHour: number;
}

interface BuildOpts {
  // Cumulative series carry the running total forward through any client-side
  // gap (the engine zero-fills, so this is a robustness net, not the norm).
  // Use this when the ROWS ARE ALREADY cumulative (the engine's CumulativeHourly).
  cumulative?: boolean;
  // Build the running total HERE, from per-hour rows — each slot adds to the
  // previous one, per series (so a by-day series resets at every day). Used by
  // the By day view, which always fetches the per-hour report type (RP-2175).
  // Mutually exclusive with `cumulative`; this one wins.
  runningTotal?: boolean;
  metric?: MetricKey;
}

// 24 points in boundary→boundary order; one series per day when rows are
// day-grouped, else a single aggregate series. Hours the engine didn't send
// (defensive — it zero-fills) read $0, or carry forward in cumulative mode.
export function buildHourlySeries(data: ReportResponse, opts?: BuildOpts): HourlySeries {
  const metric = opts?.metric ?? "NetSales";
  const boundaryHour = boundaryHourFrom(data.window.dayBoundary);
  const perDay = data.rows.some((r) => r.group != null);

  // series key -> (clock hour -> value)
  const bySeries = new Map<string, Map<number, number>>();
  if (perDay) {
    const days = [...new Set(data.rows.map((r) => r.group ?? ""))].sort();
    for (const day of days) bySeries.set(dayLabel(day), new Map());
    for (const r of data.rows) {
      bySeries.get(dayLabel(r.group ?? ""))?.set(hourFromRow(r), r.values[metric] ?? 0);
    }
  } else {
    const hours = new Map<number, number>();
    for (const r of data.rows) hours.set(hourFromRow(r), r.values[metric] ?? 0);
    bySeries.set(AGGREGATE_SERIES_KEY, hours);
  }

  const seriesKeys = [...bySeries.keys()];
  const carried = new Map<string, number>(); // cumulative fill-forward per series
  const points: HourlyPoint[] = [];
  for (let slot = 0; slot < 24; slot++) {
    const hour = (boundaryHour + slot) % 24;
    const point: HourlyPoint = { slot, hour, label: hourLabel(hour) };
    for (const key of seriesKeys) {
      const raw = bySeries.get(key)?.get(hour);
      const value = opts?.runningTotal
        ? (carried.get(key) ?? 0) + (raw ?? 0) // per-hour rows -> running total, per series
        : raw ?? (opts?.cumulative ? carried.get(key) ?? 0 : 0);
      carried.set(key, value);
      point[key] = value;
    }
    points.push(point);
  }

  return { points, seriesKeys, perDay, boundaryHour };
}

// Grid shaping for the AGGREGATE hourly response: reorder the engine's 0..23
// rows into boundary→boundary order with uniform "5 AM" labels (the engine
// zero-fills; the fill here is a robustness net). Cumulative gaps carry
// forward instead of reading $0.
export function zeroFillHourlyRows(
  data: ReportResponse,
  opts?: { cumulative?: boolean }
): ReportResponse {
  if (data.rows.some((r) => r.group != null)) return data;

  const boundaryHour = boundaryHourFrom(data.window.dayBoundary);
  const byHour = new Map(data.rows.map((r) => [hourFromRow(r), r]));

  const carried: Partial<Record<MetricKey, number>> = {};
  const rows = Array.from({ length: 24 }, (_, slot) => {
    const hour = (boundaryHour + slot) % 24;
    const existing = byHour.get(hour);
    const values: Partial<Record<MetricKey, number>> = {};
    for (const m of data.metrics) {
      const raw = existing?.values[m];
      values[m] = raw ?? (opts?.cumulative ? carried[m] ?? 0 : 0);
      carried[m] = values[m];
    }
    return { key: String(hour), label: hourLabel(hour), values, group: null };
  });

  return { ...data, rows };
}

// Turn PER-HOUR by-day rows into per-day RUNNING TOTALS: every metric accumulates
// along the business day (boundary→boundary) and RESETS at each day block, so a
// day's last hour equals that day's total. Row order and labels are untouched —
// only the values change.
//
// The By day view drives cumulative from here rather than from the engine's
// CumulativeHourly: on dimension=Day responses the engine sends the per-hour
// values through unaccumulated (contract §3 says the running total resets per day
// block; it does not apply one), which is why "By day + Cumulative" rendered
// per-hour numbers. Accumulating the per-hour Net client-side keeps the underlying
// figures identical — it only changes per-hour vs running-total presentation.
export function accumulateByDayRows(data: ReportResponse): ReportResponse {
  if (!data.rows.some((r) => r.group != null)) return data;

  const boundaryHour = boundaryHourFrom(data.window.dayBoundary);
  // Business-day slot: 0 at the boundary hour, 23 at the hour before it.
  const slotOf = (r: ReportRow) => (hourFromRow(r) - boundaryHour + 24) % 24;

  // rows[].values may carry MORE keys than metrics[] (suppressed-but-available,
  // e.g. Gross) and the page can opt those back in — accumulate them too, or the
  // grid would mix running-total Net with per-hour Gross.
  const metricKeys = new Set<MetricKey>(data.metrics);
  for (const r of data.rows) {
    for (const k of Object.keys(r.values)) metricKeys.add(k as MetricKey);
  }

  // Accumulate in clock order within each day, but write back into the row's
  // original position so the engine's block order survives.
  const byDay = new Map<string, number[]>();
  data.rows.forEach((r, i) => {
    const day = r.group ?? "";
    const idxs = byDay.get(day);
    if (idxs) idxs.push(i);
    else byDay.set(day, [i]);
  });

  const values = data.rows.map((r) => ({ ...r.values }));
  for (const idxs of byDay.values()) {
    const running: Partial<Record<MetricKey, number>> = {};
    const ordered = [...idxs].sort((a, b) => slotOf(data.rows[a]) - slotOf(data.rows[b]));
    for (const i of ordered) {
      for (const m of metricKeys) {
        running[m] = (running[m] ?? 0) + (data.rows[i].values[m] ?? 0);
        values[i][m] = running[m];
      }
    }
  }

  return { ...data, rows: data.rows.map((r, i) => ({ ...r, values: values[i] })) };
}

// Grid shaping for the BY-DAY hourly response: keep the engine's row order
// (one 5 AM→4 AM block per day) but relabel for humans — "5 AM" hours and
// the old reports' date style ("Sat, Jun 28, 2026") on the day column.
// `cumulative` builds the per-day running total first (see accumulateByDayRows).
export function formatByDayGridRows(
  data: ReportResponse,
  opts?: { cumulative?: boolean }
): ReportResponse {
  if (!data.rows.some((r) => r.group != null)) return data;
  const src = opts?.cumulative ? accumulateByDayRows(data) : data;
  return {
    ...src,
    rows: src.rows.map((r) => ({
      ...r,
      label: hourLabel(hourFromRow(r)),
      group: r.group ? formatDay(r.group) : r.group,
    })),
  };
}
